<template>
    <v-dialog v-model="dialog" max-width="700" persistent>
        <template v-slot:activator="{ on: dialogEvents, attrs }">
            <template v-if="$scopedSlots.activator">
                <slot name="activator" :on="dialogEvents" :attrs="attrs" :open="openDialog"></slot>
            </template>
            <v-btn v-else color="error" text v-bind="attrs" v-on="dialogEvents">{{ defaultButtonText }}</v-btn>
        </template>

        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <span class="secondary--text">{{ dialogTitle }}</span>
                <v-spacer></v-spacer>
                <v-btn :disabled="deleting" @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle v-if="isSpace || isSnapshot">
                <div>{{ subtitleText }}</div>
            </v-card-subtitle>
            <v-card-text>
                <v-alert color="error" prominent type="error" text>
                    <span class="font-weight-bold">{{ consequencesTitle }}</span>
                    <ul class="mt-3">
                        <li class="subtitle-2" v-for="(item, index) in consequences" :key="index">{{ item }}</li>
                    </ul>
                    <v-divider class="mt-4 error"></v-divider>
                    <v-checkbox light color="error" v-model="agreeBox" :e2e-snapshot-delete-confirm="isSnapshot ? true : null">
                        <template v-slot:label>
                            <span>I understand</span>
                        </template>
                    </v-checkbox>
                </v-alert>

                <v-alert v-if="isSpace" :value="error" type="error" class="mt-4" text>
                    <v-row align="center">
                        <v-col class="grow">
                            <div>
                                <b>{{ errorContent }}</b>
                            </div>
                            <span>For more information on space deletion issues, check the troubleshooting documentation.</span>
                        </v-col>
                        <v-col class="shrink">
                            <v-btn
                                small
                                outlined
                                color="error"
                                href="https://docs.nuvolos.com/troubleshooting/troubleshooting/administration-troubleshooting/i-cant-delete-a-space"
                                target="_blank">
                                go to documentation
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-alert>
                <v-alert v-if="isSnapshot && error" color="error" prominent type="error" class="mt-4" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Snapshot Deletion Error</span>
                        An error has occurred and the snapshot could not be deleted. Please try again later and if the problem persists contact
                        support@alphacruncher.com
                    </div>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <DeleteButton
                    v-if="isSpace"
                    :objectType="nuvolosObjectTypes.SPACE"
                    :objectName="resolvedSpaceName"
                    :isAsyncDeletion="true"
                    buttonName="Delete space"
                    :apiURL="`/spaces/${activeSpaceSid}`"
                    :id="activeSpaceSidNumber"
                    :higherLevelId="activeOrgOidNumber"
                    :routeAfter="routeAfter"
                    fetchString="orgStore/fetchOrgSpaces"
                    @error="onDeleteError($event.error)"
                    @deleting="deleting = $event.value"
                    :disabled="!agreeBox || !activeOrgOidNumber || !activeSpaceSidNumber"
                    isInsideDialog />
                <DeleteButton
                    v-else-if="isSnapshot"
                    buttonName="delete"
                    protocol="POST"
                    :objectType="nuvolosObjectTypes.SNAPSHOT"
                    apiURL="/snapshots/delete"
                    :request-body="{ snids: selectedSnapshotIds }"
                    :higherLevelId="activeInstanceIidNumber"
                    fetchString="instanceStore/fetchInstanceSnapshots"
                    :routeAfter="snapshotRouteAfter"
                    @error="onDeleteError($event.error)"
                    @deleting="onSnapshotDeleting($event)"
                    :disabled="!agreeBox || !selectedSnapshotIds.length"
                    :isInsideDialog="true"
                    :objectName="selectedSnapshotNames.toString()"
                    :isAsyncDeletion="true" />
                <v-btn v-else text color="error" :disabled="!agreeBox || deleting" :loading="deleting" @click="deleteAccount">delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'

const DeleteButton = () => import('@/components/DeleteButton')

export default {
    name: 'EntityDeleteDialog',
    mixins: [enumsData],
    components: {
        DeleteButton
    },
    props: {
        entityType: {
            type: String,
            required: true,
            validator: value => ['space', 'account', 'snapshot'].includes(value)
        },
        organizationId: {
            type: [String, Number],
            default: ''
        },
        spaceId: {
            type: [String, Number],
            default: ''
        },
        spaceName: {
            type: String,
            default: ''
        },
        snapshotData: {
            type: Array,
            default() {
                return []
            }
        }
    },
    data() {
        return {
            dialog: false,
            deleting: false,
            error: false,
            errorContent: 'An error has occurred',
            agreeBox: false
        }
    },
    computed: {
        isSpace() {
            return this.entityType === 'space'
        },
        isAccount() {
            return this.entityType === 'account'
        },
        isSnapshot() {
            return this.entityType === 'snapshot'
        },
        defaultButtonText() {
            if (this.isSpace) {
                return 'Delete space'
            }
            if (this.isSnapshot) {
                return 'Delete snapshot'
            }
            return 'Delete account'
        },
        dialogTitle() {
            if (this.isSpace) {
                return 'Space Deletion'
            }
            if (this.isSnapshot) {
                return 'Delete snapshot'
            }
            return 'Delete Account'
        },
        consequencesTitle() {
            if (this.isSpace) {
                return 'Here is what happens when you delete a space'
            }
            if (this.isSnapshot) {
                return 'Here is what happens when you delete a snapshot'
            }
            return 'Here is what happens when you delete your account'
        },
        consequences() {
            if (this.isSpace) {
                return [
                    'All files, data, and applications stored in all instances in this space will be deleted permanently.',
                    'All users of this space will lose their access and data.'
                ]
            }
            if (this.isSnapshot) {
                return ['The contents (files, tables, and application) of the snapshot will be deleted permanently.']
            }
            return [
                'You will lose all your roles in Nuvolos and you will not be able to login anymore with your account.',
                'Your instances and all their contents (files, tables, and application) will not be deleted but you will not be able to access them.',
                'Your instances and all their contents will be deleted after the grace period has ended.',
                'To gain access again, you will need to arrange a new invitation with faculty.'
            ]
        },
        activeOrgOid() {
            return this.organizationId || this.$route.params.oid || ''
        },
        activeSpaceSid() {
            return this.spaceId || this.$route.params.sid || ''
        },
        activeOrgOidNumber() {
            const parsed = Number(this.activeOrgOid)
            return Number.isNaN(parsed) ? null : parsed
        },
        activeSpaceSidNumber() {
            const parsed = Number(this.activeSpaceSid)
            return Number.isNaN(parsed) ? null : parsed
        },
        resolvedSpaceName() {
            return this.spaceName || this.$store.getters['spaceStore/currentSpaceName']
        },
        subtitleText() {
            if (this.isSpace) {
                return this.resolvedSpaceName
            }
            if (this.isSnapshot) {
                if (this.snapshotData.length > 1) {
                    return `${this.snapshotData.length} snapshots marked for deletion`
                }
                return this.selectedSnapshotNames.join(', ')
            }
            return ''
        },
        selectedSnapshotIds() {
            return this.snapshotData.map(snapshot => snapshot.snid)
        },
        selectedSnapshotNames() {
            return this.snapshotData.map(snapshot => snapshot.long_id)
        },
        activeInstanceIidNumber() {
            const parsed = Number(this.$route.params.iid)
            return Number.isNaN(parsed) ? null : parsed
        },
        snapshotRouteAfter() {
            const currentSnid = Number(this.$route.params.snid)
            const isCurrentSnapshotSelected = this.selectedSnapshotIds.includes(currentSnid)
            const developmentSnapshot = this.$store.getters['snapshotStore/developmentSnapshot']
            if (isCurrentSnapshotSelected && developmentSnapshot && developmentSnapshot.snid) {
                return {
                    name: 'snapshot-overview',
                    params: {
                        oid: this.$route.params.oid,
                        sid: this.$route.params.sid,
                        iid: this.$route.params.iid,
                        snid: developmentSnapshot.snid
                    }
                }
            }
            return null
        },
        routeAfter() {
            return {
                name: 'home-dashboard',
                params: { oid: this.activeOrgOid }
            }
        }
    },
    methods: {
        openDialog() {
            this.dialog = true
        },
        onDeleteError(hasError) {
            this.error = hasError
            if (hasError) {
                this.errorContent = 'An error has occurred'
            }
        },
        onSnapshotDeleting($event) {
            this.deleting = $event.value
            if (!this.error && !$event.value) {
                this.dialog = false
                this.agreeBox = false
                this.$emit('deleting')
            }
        },
        deleteAccount() {
            if (!this.agreeBox || this.deleting) {
                return
            }
            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Deleting your account, please wait..',
                snackBarTimeout: 10000,
                snackBarIcon: 'error'
            })
            this.deleting = true
            this.$axios
                .delete('/users/delete_from_datahub_midpoint')
                .then(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'You account has been successfully deleted!',
                        snackBarTimeout: 10000,
                        snackBarIcon: 'check_circle'
                    })
                    this.$auth.logout()
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Account deletion failed, please try again later or contact us via the chat in the bottom right side of the page.',
                        snackBarTimeout: 10000,
                        snackBarIcon: 'error'
                    })
                })
                .finally(() => {
                    this.deleting = false
                    this.dialog = false
                    this.agreeBox = false
                })
        }
    },
    watch: {
        dialog(val) {
            if (!val) {
                this.error = false
                this.agreeBox = false
                this.deleting = false
            }
        }
    }
}
</script>
